import { get, post } from '@/tools/http';
import { getImgUrl } from '@/tools/uploader';

import Vue from 'vue';

export default {
    namespaced: true,

    state: () => ({
        categories: [],
        institutes: [],
        items: [],
        popularItems: [],
        specializationItems: [],
        category: null,
        item: null,
        professions: [],
        programs: {},
        profiles: {},
        blanks: {},

        courses: [],
        webinars: [],
    }),

    mutations: {
        setCategories: (state, items) => (state.categories = items),
        setInstitutes: (state, items) => (state.institutes = items),
        setCategory: (state, item) => (state.category = item),
        setItems: (state, items) => (state.items = items),
        setPopularItems: (state, items) => (state.popularItems = items),
        setSpecializationItems: (state, items) => (state.specializationItems = items),
        setItem: (state, item) => (state.item = item),
        setProfessions: (state, professions) =>
            (state.professions = professions),
        setEducation: (state, items) => {
            Vue.set(state.item, 'educationInstitutions', items);
        },
        addProgram: (state, program) => {
            state.programs = {
                ...state.programs,

                [program.id]: {
                    createdAt: Date.now(),
                    value: program,
                },
            };
        },
        // add Related to education program Professions or Specializations
        addProfiles: (state, { programId, profiles }) => {
            state.profiles = {
                ...state.profiles,

                [programId]: {
                    createdAt: Date.now(),
                    value: profiles,
                },
            };
        },
        addProgramCompetences: (state, { programId, competences }) => {
            const soft = [];
            const hard = [];
            competences.forEach(item =>
                item.competenceType === 'soft'
                    ? soft.push(item)
                    : hard.push(item)
            );
            state.competences = {
                [programId]: { hard, soft },
            };
        },
        setBlanks: (state, blanks) => {
            const reducer = (acc, cur) => {
                acc[cur.id] = cur;

                return acc;
            };

            state.blanks = blanks.reduce(reducer, {});
        },

        addActivity: (state, { activity, type }) => {
            let finder = state[`${type}s`].find(a => a.id == activity.id);
            if (!finder) {
                state[`${type}s`].push(activity);
            }
        },
    },

    actions: {
        async loadInstitutes(ctx, params = {}) {
            const res = await get('progress', 'institutes', params);
            ctx.commit('setInstitutes', res['institutes'] || []);
        },
        async loadBlanks(ctx, instituteId) {
            const res = await get('progress', 'institutes/programs', {
                'filter[id]': instituteId,
                
            });

            ctx.commit('setBlanks', res['institutes'] || []);
        },
        async loadProgram(ctx, id) {
            const path = `api/v2/programs/${id}`;
            const program = await get('progress', path);
            ctx.commit('addProgram', program);
        },
        async loadProgramCompetences(ctx, programId) {
            const path = `api/v2/programs/${programId}/competences`;
            const res = await get('progress', path);
            const competences = res.items;
            ctx.commit('addProgramCompetences', { programId, competences });
        },
        async loadProfiles(ctx, programId) {
            const path = `profiles?filter[educationalProgram]=${programId}`;
            const res = await get('progress', path);

            const profiles = res['profiles'];
            ctx.commit('addProfiles', { programId, profiles });
        },
        async loadActivity(ctx, { type, foreignActivityId: id }) {
            const subPath = {
                course: `course?id=${id}`,
                webinar: `webinar?id=${id}`,
            }[type];

            if (!subPath) {
                return;
            }

            const path = 'entity/select/' + subPath;
            const res = await get('cms', path);
            if (res) {
                if (res.list.length) {
                    let activity = { ...res.list[0] };
                    activity.preview = await getImgUrl(
                        res.list[0].preview,
                        ctx.rootGetters.img_stub
                    );
                    ctx.commit('addActivity', {
                        activity: activity,
                        type: type,
                    });
                }
            }
        },
        async loadActivities(ctx, id) {
            const path = `profiles?filter[id]=${id}`;
            const res = await get('progress', path);

            const reducer = (acc, cur) => {
                if (!acc.some(a => a['activityId'] == cur['activityId'])) {
                    acc.push(cur);
                }

                return acc;
            };

            res['profiles']
                .flatMap(p => p['competencies'])
                .flatMap(c => c['competencyActivities'])
                .reduce(reducer, [])
                .forEach(a => ctx.dispatch('loadActivity', a));
        },
        loadCategories: async ctx => {
            ctx.commit('setCategories', []);
            let items = (await get('progress', 'professions')).professions;
            ctx.commit('setCategories', items);
        },
        loadCategory: async (ctx, id) => {
            if (ctx.state.category && ctx.state.category.id === parseInt(id)) {
                return;
            }
            ctx.commit('setCategory', null);
            let item = ctx.state.categories.find(v => v.id === parseInt(id));
            if (item === undefined) {
                let items = (
                    await get('progress', 'professions', { 'filter[id]': id })
                ).professions;
                item =
                    items.length > 0
                        ? items.find(v => v.id === parseInt(id))
                        : null;
            }
            ctx.commit('setCategory', item);
        },
        loadItems: async (
            ctx,
            { professionId = null, limit = null, offset = null } = {}
        ) => {
            ctx.commit('setItems', []);
            let params = {
                'filter[status]': 'published',
                'filter[entityType]': 'profession',
            };
            if (professionId) {
                params['filter[profession]'] = professionId;
            }
            if (limit !== null && offset !== null) {
                params['limit'] = limit;
                params['offset'] = offset;
            }
            let items = (await get('progress', 'profiles-list', params))
                .profiles;
            ctx.commit('setItems', items);
        },
        loadPopularItems: async (ctx, { limit = null, offset = null }) => {
            const params = {
                'filter[status]': 'published',
                'filter[isPopular]': true,
                'filter[entityType]': 'profession',
                limit,
                offset,
            };
            const answer = await get('progress', 'profiles', params);
            const items = answer.profiles;
            ctx.commit('setPopularItems', items);
        },
        async loadSpecializationItems({ commit }, professionId) {
            commit('setSpecializationItems', []);
            const answer = await get('progress', `api/v2/profiles/${professionId}/specializations`);
            const items = answer.items;
            commit('setSpecializationItems', items);
        },
        loadItem: async (ctx, id) => {
            if (ctx.state.item && ctx.state.item.id === parseInt(id)) {
                return;
            }
            ctx.commit('setItem', null);
            let items = (
                await get('progress', 'profiles', {
                    'filter[status]': 'published',
                    'filter[id]': id,
                })
            ).profiles;
            let item =
                items.length > 0
                    ? items.find(v => v.id === parseInt(id))
                    : null;
            if (item === null) {
                return;
            }
            ctx.dispatch('loadCategory', item['professionId']);

            let competences = (
                await Promise.all(
                    item.competencies
                        .map(v => v.id)
                        .map(v =>
                            get('progress', 'api/v1/competence/select/' + v)
                        )
                )
            ).filter(v => v);

            competences = competences.reduce((acc, v) => {
                if (!acc[v['typeId']]) {
                    acc[v['typeId']] = {
                        id: v['typeId'],
                        name: null,
                        items: [],
                    };
                }
                acc[v['typeId']].items.push({
                    id: v['id'],
                    name: v['name'],
                });
                return acc;
            }, {});

            (
                await Promise.all(
                    Object.values(competences)
                        .filter(v => v.name === null)
                        .map(v => v.id)
                        .map(v => get('progress', 'api/v1/type/select/' + v))
                )
            )
                .filter(v => v)
                .forEach(v => {
                    competences[v.id].name = v.name;
                    competences[v.id].type = v.key;
                });

            item.competences = Object.values(competences).filter(v => v.name);

            ctx.commit('setItem', item);

            ctx.dispatch('loadEducations');
        },
        loadEducations: async ctx => {
            if (ctx.state.item && ctx.state.item.id) {
                let res = await get(
                    'progress',
                    `profile/${ctx.state.item.id}/institutes/programs`
                );
                if (res) {
                    ctx.commit('setEducation', res);
                }
            }
        },

        loadAllProfession: async ctx => {
            let res = await get('progress', 'profiles', {
                'filter[status]': 'published',
            });
            if (res) {
                ctx.commit('setProfessions', res.profiles);
            }
        },

        loadProfession: (ctx, id) => {
            return get('progress', 'profiles', {
                'filter[status]': 'published',
                'filter[id]': id,
            });
        },

        addingProfession: async (ctx, professionId) => {
            let res = await post(
                'progress',
                'user/profile/add',
                {
                    profile: professionId,
                },
                true
            );
        },
        checkAddingProfession: (ctx, professionId) => {
            return get('progress', `user/profile/isSet/${professionId}`);
        },
    },

    getters: {
        top4Items: state => state.items.slice(0, 4),
        last2Items: state => state.items.slice(0, 2),
        popular4Items: state => state.popularItems.slice(0, 4),
        getSpecializationItems: state => state.specializationItems,
        categories: state => state.categories,
        category: state => state.category,
        items: state => state.items,
        item: state => state.item,
        getAllProfession: state => state.professions,

        blanksByInstitute: state => instId => {
            const inst = state.blanks[instId];
            
            if (!inst) {
                return [];
            }

            return inst.programs.map(p => ({ id: p.id, name: p.name, logo: inst.logo, entityType: p.entityType }));
        },
        getProfessionCourses: state => state.courses,
        getProfessionWebinars: state => state.webinars,
        getInstitutes: state => state.institutes,
        getRelatedProfessionsByProgramId: state => id => {
            const profile = state.profiles[id];
            const professions = (!profile) ? [] : profile.value;
            return professions;
        },
        getRelatedProfessionsByProgramIdList: (_, getters) => id => getters.getRelatedProfessionsByProgramId(id).map(profession => profession.id),
        getCompetences: state => state.competences,
        getCompetencesById: state => (id) => state.competences && state.competences[id],
    },
};
